import { useContext, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import { ProgressButtons, useNotificationContext } from "cerulean";
import { ResponsiveFlex } from "@narmi/design_system";
import { v4 as uuidv4 } from "uuid";
import { cents, entities, modules } from "byzantine";
import filters from "byzantine/src/filters";
import Account from "byzantine/src/Account";
import {
  getIsPastACHCutoff,
  getIsOneTimePaymentForToday,
  useMappedEntryDescription,
} from "byzantine/src/dbbl/businessLogic/modules/achPayments";
import { useIsACHDualApprovalRequired } from "byzantine/src/dbbl/businessLogic/entities/users/hooks";
import containerStyles from "./InvoiceContainer.module.scss";
import styles from "../ach/ReviewScreen.module.scss";
import {
  INVOICE_ROUTE,
  INVOICE_DETAILS_ROUTE,
  INVOICE_RECEIPT_ROUTE,
} from "./InvoiceRoutes";
import { useInvoiceFlow } from "./InvoiceFlowContext";
import { RowWrapper } from "../../PaymentActivity/CreateBulk/ReviewBulkScreen";
import AccountContext from "../../contexts/AccountContext";
import { useInstitutionSettings } from "../../contexts/InstitutionSettingsContext";

const Section = ({
  heading,
  children,
}: {
  heading: string;
  children: React.ReactNode;
}) => (
  <div className={styles.reviewCardSectionBordered}>
    <ResponsiveFlex gapSize="m">
      <h5 className="fontSize--xs fontWeight--bold">{heading.toUpperCase()}</h5>
      {children}
    </ResponsiveFlex>
  </div>
);

const ReviewStep = () => {
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const { previewUrl, uploadedFileName, invoice, deliveryOptionDisplay } =
    useInvoiceFlow();
  const { accounts } = useContext(AccountContext);
  const { sendNotification } = useNotificationContext();
  const institution = useInstitutionSettings();

  const form = modules.achPayments.ACHPaymentForm.useForm();
  const { createACHPayment, loading: achLoading } =
    modules.achPayments.useCreateACHPayment();
  const { createFedNowPayment, loading: fedNowLoading } =
    modules.achPayments.useCreateFedNowPayment();
  const { send: fetchACHCompanies } =
    entities.achCompanies.useFetchACHCompanies();
  const achCompany = entities.achCompanies.useACHCompany();
  const selectedRecipient = entities.recipients.useSelectedRecipient();
  const isInstantDelivery = modules.achPayments.useIsInstantACHDelivery();
  const isSameDayDelivery = modules.achPayments.useIsSameDayACHDelivery();
  const achDualApprovalRequired = useIsACHDualApprovalRequired();
  const { getSelectedEntryDescription } = useMappedEntryDescription();

  const loading = achLoading || fedNowLoading;

  const { values } = form;
  const {
    amount,
    fromAccount,
    secCode,
    memo,
    startDate,
    entryDesc,
    description,
  } = values;

  const senderAccountData = accounts.find(
    (account: Account) => account.id === fromAccount
  );

  useEffect(() => {
    fetchACHCompanies(`institution_account=${senderAccountData?.id}`);
  }, [senderAccountData, fetchACHCompanies]);

  const idempotencyKey = useMemo(() => uuidv4() as UUID, []);

  if (!senderAccountData || !selectedRecipient) {
    return null;
  }

  const senderAccount = new Account(senderAccountData);
  const { name, nickname, ach_destination: achDestination } = selectedRecipient;
  const isPastCutoff = getIsPastACHCutoff(institution.ach_next_cutoff);

  let fallbackDeliverySpeed = `${l10n.getString("label-standard")} ACH`;
  if (isInstantDelivery) {
    fallbackDeliverySpeed = l10n.getString("label-instant");
  } else if (isSameDayDelivery) {
    fallbackDeliverySpeed = l10n.getString("label-same-day");
  }

  let fallbackDeliveryArrival = l10n.getString("label-ach-speed-short");
  if (isInstantDelivery) {
    fallbackDeliveryArrival = l10n.getString("label-instant");
  } else if (isSameDayDelivery) {
    fallbackDeliveryArrival = isPastCutoff
      ? l10n.getString(
          "label-same-day-delivery-speed-arrival-next-business-day"
        )
      : l10n.getString("label-same-day-delivery-speed-arrival");
  }

  const onBack = () =>
    navigate(`/transfer/${INVOICE_ROUTE}/${INVOICE_DETAILS_ROUTE}`);

  const handleSubmit = () => {
    const props = {
      invoice: invoice?.uuid,
      onSuccess: (
        data: API.ACHPayment | API.ScheduledACHPayment | API.FedNowTransaction
      ) => {
        let paymentModel:
          | "FedNowTransaction"
          | "UnverifiedACHTransaction"
          | "UnverifiedACHTransfer" = "UnverifiedACHTransaction";

        if (isInstantDelivery) {
          paymentModel = "FedNowTransaction";
        } else if ("next_transfer_at" in data) {
          paymentModel = "UnverifiedACHTransfer";
        }

        navigate(
          `/transfer/${INVOICE_ROUTE}/${INVOICE_RECEIPT_ROUTE}/${data.id}`,
          {
            state: {
              deliverySpeed:
                deliveryOptionDisplay?.arrival || fallbackDeliveryArrival,
              deliveryFee:
                deliveryOptionDisplay?.fee || l10n.getString("label-no-fee"),
              speed: deliveryOptionDisplay?.label || fallbackDeliverySpeed,
              paymentId: data.id,
              paymentModel,
            },
          }
        );
      },
      onError: ({ message }: { message: string }) => {
        sendNotification({ type: "negative", text: message });
      },
    };

    if (isInstantDelivery) {
      createFedNowPayment(idempotencyKey, props);
      return;
    }

    createACHPayment(idempotencyKey, props);
  };

  const getReviewSubtitle = () => {
    if (isInstantDelivery) {
      return l10n.getString(
        "single-ach-payment-review-sub-title-credit-instant"
      );
    }
    if (isSameDayDelivery) {
      return l10n.getString(
        "single-ach-payment-review-sub-title-credit-same-day"
      );
    }
    return l10n.getString("single-ach-payment-review-sub-title-credit");
  };

  const displaySendDate =
    getIsOneTimePaymentForToday(values, institution.ach_next_cutoff) &&
    achDualApprovalRequired
      ? l10n.getString("pending-table-one-time-payment")
      : startDate;

  const downloadLink = (
    <a
      className="fontWeight--semibold"
      href={previewUrl ?? "#"}
      download={uploadedFileName ?? `invoice-${invoice?.uuid ?? ""}`}
    >
      <span className="narmi-icon-download" aria-hidden="true" />
      <span className="margin--left--xxs">
        {l10n.getString("invoice-review-download-invoice")}
      </span>
    </a>
  );

  return (
    <div className={containerStyles.reviewColumn}>
      <ResponsiveFlex gapSize="xl">
        <h1 className="fontFamily--heading fontSize--heading1">
          {l10n.getString("review-your-payment-heading")}
        </h1>

        <div className={styles.reviewCardContainer}>
          <div className={styles.reviewCardHeader}>
            <p>
              {getReviewSubtitle()} {name}{" "}
              {nickname ? (
                <span className="fontColor--secondary fontSize--xs">{`(${nickname})`}</span>
              ) : null}
            </p>
            <h1>{filters.currency(cents(amount))}</h1>
          </div>

          <Section heading={l10n.getString("ach-payment-payment-details")}>
            <RowWrapper
              heading={l10n.getString("single-ach-payment-from-account")}
              content={senderAccount.getShortDescription()}
            />
            {achCompany?.company_id && !isInstantDelivery ? (
              <RowWrapper
                heading={l10n.getString("ach-payment-review-company")}
                content={`${achCompany.company_name} / ${achCompany.company_id}`}
              />
            ) : null}
            {!isInstantDelivery ? (
              <RowWrapper
                heading={l10n.getString("ach-payment-transaction-title")}
                content={`${secCode}`}
              />
            ) : null}
            {entryDesc && !isInstantDelivery ? (
              <RowWrapper
                heading={l10n.getString("label-entry-description")}
                content={getSelectedEntryDescription(entryDesc)}
              />
            ) : null}
            <RowWrapper
              heading={l10n.getString("ach-payment-review-date")}
              content={displaySendDate}
            />
            <RowWrapper
              heading={l10n.getString("invoice-review-invoice-label")}
              content={downloadLink}
            />
          </Section>

          <Section heading={l10n.getString("ach-payment-recipient-details")}>
            <RowWrapper
              heading={l10n.getString("ach-payment-recipient-title")}
              content={name}
            />
            <RowWrapper
              heading={l10n.getString("recipient-card-financial-institution")}
              content={achDestination?.institution_name}
            />
            <RowWrapper
              heading={l10n.getString("ach-payment-review-account")}
              content={achDestination?.account_number}
            />
            <RowWrapper
              heading={l10n.getString("ach-payment-review-routing")}
              content={achDestination?.routing_number}
            />
          </Section>

          {description || memo ? (
            <Section heading={l10n.getString("ach-payment-internal-details")}>
              {description ? (
                <RowWrapper
                  heading={l10n.getString("label-memo")}
                  content={description}
                />
              ) : null}
              {memo ? (
                <RowWrapper
                  heading={l10n.getString("label-note")}
                  content={memo}
                />
              ) : null}
            </Section>
          ) : null}
        </div>

        <ProgressButtons
          backLabel={l10n.getString("button-back")}
          nextLabel={l10n.getString("ach-payment-review-action")}
          onBack={onBack}
          onNext={handleSubmit}
          disabled={loading}
          disableNext={loading}
          isNextLoading={loading}
        />
      </ResponsiveFlex>
    </div>
  );
};

export default ReviewStep;
